import { useMemo, useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ApexOptions } from 'apexcharts';
import ReactApexcharts from 'src/@core/components/react-apexcharts';
import buildFilterString from '../../helpers/buildFilterString';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { SalesOrderDto, SalesOrderRevenueDto } from 'src/models/salesorder';
import { useGetRevenueCodesQuery, useGetSalesOrderRevenueQuery } from 'src/queries/services/salesorder';
import { getSalesOrders } from 'src/services/salesorder';

const RevenueWidget = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }) => {
  const [series, setSeries] = useState<number[]>([]);
  const [orderIdArray, setOrderIdArray] = useState<string[] | undefined>(undefined);

  const theme = useTheme();
  const prod = useGetProductLevel();

  const title = prod === Products.MyPortal ? ' Costs' : 'Revenue';

  /**
   * Revenue Code Query
   * - Gets all available revenue codes
   * - Converts response from RevenueCodeDto[] to Record<id, description>
   */
  const { data: revenueCodes } = useGetRevenueCodesQuery({
    initialData: [],
    staleTime: Infinity,
    customResponseModifier: data => {
      return data.reduce((acc, curr) => {
        return { ...acc, [curr.id as string]: curr.description };
      }, {});
    }
  });

  const filterString = useMemo(
    () => buildFilterString(orderByValue, dashboardDateStrings),
    [orderByValue, dashboardDateStrings]
  );

  /**
   * Get Sales Orders Query
   * - Gets all sales orders for the given orderByValue and date range
   * - Sets the orderIdArray on success
   * - If no sales orders are found, the series is set to all zero's
   */
  useQuery({
    queryKey: ['revenueWidget', filterString],
    queryFn: async () => {
      const res = await getSalesOrders({ filter: filterString });

      return res.data;
    },
    enabled: Boolean(revenueCodes),
    onSuccess: data => {
      const orderIds = (data as SalesOrderDto[])?.map(order => order?.id ?? '');

      if (orderIds.length === 0) {
        return setSeries(Array(Object.values(revenueCodes ?? {}).length).fill(0));
      }

      setOrderIdArray(orderIds);
    }
  });

  /**
   * Order Revenue Query
   * - Gets all sales order revenue for sales orders which have an id in the orderIdArray
   * - Disabled while the orderIdArray is empty
   * - Will set the computed series on success
   * - Will reset the orderIdArray on success
   */
  useGetSalesOrderRevenueQuery({
    params: {
      filter: orderIdArray?.map((id: string) => `salesOrderId eq guid'${id}'`).join(' or ') ?? ''
    },
    enabled: orderIdArray && orderIdArray.length > 0,
    initialData: Array(Object.values(revenueCodes ?? {}).length).fill(0),
    onSuccess: data => {
      const computedSeries = buildSeries(data);

      setSeries(computedSeries);
      setOrderIdArray(undefined);
    }
  });

  /**
   * Build value series for chart e.g [1, 2, 3]
   * - Values are mapped to the order of the keys in the revenue codes
   */
  const buildSeries = (data: SalesOrderRevenueDto[]) => {
    return Object.keys(revenueCodes ?? {}).map(revCodeId => {
      return data
        .filter(entry => entry.revenueCodeId === revCodeId)
        .reduce((acc, curr) => {
          return Number(acc + (curr?.value ?? 0));
        }, 0);
    });
  };

  /**
   * Chart options
   */
  const options: ApexOptions = {
    stroke: { width: 2, colors: [theme.palette.background.paper] },
    chart: { type: 'donut' },
    labels: Object.values(revenueCodes ?? {}) as string[],
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val: number) => {
          return `${val.toFixed(2)}`;
        }
      }
    },
    plotOptions: {
      pie: {
        customScale: 1,
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '1.5rem',
              fontFamily: 'Quicksand, sans-serif',
              formatter: (val: string) => {
                return `${val}`;
              }
            },
            value: {
              fontFamily: 'Quicksand, sans-serif',
              fontSize: '1.6rem',
              fontWeight: 500,
              formatter(val: string) {
                return `£${Number(val).toFixed(2)}`;
              }
            },
            total: {
              show: true,
              label: 'Total',
              fontFamily: 'Quicksand, sans-serif',
              fontSize: '1.2rem',
              fontWeight: 500,
              color: theme.palette.mode === 'dark' ? '#fff' : 'initial',
              formatter: w => {
                return `£${w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toFixed(2)}`;
              }
            }
          }
        }
      }
    },
    noData: {
      text: 'No data'
    },
    legend: { show: true, horizontalAlign: 'right', position: 'right' }
  };

  return (
    <Card sx={{ width: '100%', height: '100%' }}>
      <CardContent sx={{ height: '100%' }}>
        <Stack direction="column" sx={{ height: '100%' }}>
          <Box>
            <Typography sx={{ textAlign: 'left', fontSize: '1.571rem', fontWeight: '600' }}>{title}</Typography>
          </Box>

          <Box
            sx={{
              height: '80%',
              position: 'relative',
              '& .apexcharts-legend': {
                justifyContent: 'flex-end !important'
              },
              '& .apexcharts-datalabel-value': {
                fill: theme.palette.mode === 'dark' ? '#fff !important' : 'initial'
              },
              '& .apexcharts-legend-series': {
                textAlign: 'left',
                '& > .apexcharts-legend-text': {
                  fontSize: '1.143rem !important',
                  marginLeft: '5px',
                  ...(theme.palette.mode === 'dark' && { color: '#fff !important' })
                }
              }
            }}>
            <ReactApexcharts options={options} series={series} type="donut" height="100%" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RevenueWidget;
